import isUndefined from 'lodash.isundefined';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

// components
import CartItemEventDiscountUpsell from './CartItemEventDiscountUpsell';
import CartItemEventPrices from './CartItemEventPrices';
import CartItemEventSessions from './CartItemEventSessions';
import CartUpsell from '../CartUpsell';
import CartUpsellEventModal from '@modals/CartUpsellEventModal';
import NotificationBanner, { notificationTypes } from '@molecules/NotificationBanner';
import { BodyMedium, BodySmall, DividerLightGreyStyled, LabelSmall, LinkText } from '@styled';

// functionality
import { CartItemEventContext } from './CartItemEventProvider';
import { useDeleteCartItemModal } from '../../../../modals/DeleteCartItemModal';
import { LoadingContext } from '@context/LoadingProvider';
import { deleteCartItem } from '@general/api/cartAPI';
import { getCityStateDisplay } from '@general/helpers/address.helpers';
import colors from '@general/colors';
import { getDateRangeTextInEastern } from '@general/dates';
import productTypes from '@general/productTypes';
import useEventFromGraph from '@hooks/useEventFromGraph';
import useCartClearFlashNoticesUpdateCart from '@hooks/useCartClearFlashNoticesUpdateCart';
import { useScreenMinMd, useScreenSmDown } from '@hooks/useResponsive';
import useViewer from '@hooks/useViewer';
import { getMemberStatusText } from '@general/helpers/member.helpers';
import { addMembershipToCart, getDoesCartHaveMemberships, membershipYears } from '@general/helpers/cart.helpers';

const ImageStyled = styled.img`
    height: 64px;
    object-fit: cover;
    width: 64px;
`;

const ExtendLinkStyled = styled(LabelSmall)`
    color: ${colors.azure};
    text-decoration: underline;
`;

const RemoveLinkStyled = styled(LabelSmall)`
    color: ${colors.azure};
    text-decoration: underline;
`;

const UpsellContainerStyled = styled.div`
    margin-top: 9px;
`;

const UpsellDividerStyled = styled(DividerLightGreyStyled)`
    margin-bottom: 14px;
    margin-top: 14px;
`;

const SessionInfoTextStyled = styled(LinkText)`
    display: block;
    opacity: 1 !important;

    &.disabled {
        color: ${colors.mediumGray} !important;
    }
`;

const HeaderElements = ({
    cartItems,
    cityState,
    eventDataForMember,
    eventFromAPI,
    fullProgramCartItem,
    fullProgramRequired,
    handleDuesPromptClick,
    hasMembershipsInCart,
    isLoading,
    onRemoveClick,
    seatsRemaining,
    setIsLoading,
}) => {
    const { clearFlashNoticesRefetchCart } = useCartClearFlashNoticesUpdateCart();
    const [showDuesPrompt, setShowDuesPrompt] = useState(false);

    useEffect(() => {
        eventHasAddDuesPrompt();
    }, [eventDataForMember, eventHasAddDuesPrompt]);

    const eventHasAddDuesPrompt = useCallback(() => {
        if (!cartItems) return false;

        // Check if event has add dues prompt
        const hasAddDuesPrompt = eventDataForMember.sessions.some(session =>
            session.prompts.some(prompt => prompt.action === 'add_dues')
        );

        // If cart has dues prompt, and there's no membership in the cart, show the add membership text
        if (hasAddDuesPrompt && !hasMembershipsInCart) {
            return setShowDuesPrompt(true);
        }

        setShowDuesPrompt(false);
    }, [cartItems, eventDataForMember, hasMembershipsInCart]);

    const hasSeatsRemaining = seatsRemaining !== null && seatsRemaining >= 0;

    return (
        <div>
            <BodySmall className="mt-0.25rem">
                {getDateRangeTextInEastern(eventFromAPI.begins_at, eventFromAPI.ends_at)}
                {cityState.length > 0 && ` - ${cityState}`}
            </BodySmall>

            <div>
                {showDuesPrompt ? (
                    <ExtendLinkStyled
                        as="a"
                        href="#"
                        onClick={async e => {
                            e.preventDefault();
                            setIsLoading(true);
                            await handleDuesPromptClick();
                            setIsLoading(false);
                        }}
                    >
                        Extend membership to receive a discount
                    </ExtendLinkStyled>
                ) : null}
            </div>

            {fullProgramRequired && hasSeatsRemaining ? (
                <SessionInfoTextStyled as="span" className="text-dark-gray">
                    Seats Remaining: {seatsRemaining}
                </SessionInfoTextStyled>
            ) : null}

            {!eventDataForMember.sessionsOnly && !eventDataForMember.optionalFullProgram && (
                <RemoveLinkStyled
                    as="a"
                    href="#"
                    className="mt-0.5rem"
                    onClick={e => {
                        e.preventDefault();
                        onRemoveClick();
                    }}
                >
                    Remove
                </RemoveLinkStyled>
            )}

            {(eventDataForMember.sessionsOnly || eventDataForMember.optionalFullProgram) && (
                <div className="mt-0.5rem">
                    <NotificationBanner notificationType={notificationTypes.INFO}>
                        Register for an optional session below.
                    </NotificationBanner>
                </div>
            )}

            {fullProgramCartItem?.meta_info?.flash_notice && !isLoading && (
                <div className="mt-0.5rem">
                    <NotificationBanner
                        handleDismiss={async () => {
                            setIsLoading(true);
                            await clearFlashNoticesRefetchCart();
                            setIsLoading(false);
                        }}
                        notificationType={notificationTypes.ERROR}
                    >
                        {fullProgramCartItem.meta_info.flash_notice}
                    </NotificationBanner>
                </div>
            )}
        </div>
    );
};

HeaderElements.propTypes = {
    cartItems: PropTypes.arrayOf(PropTypes.object).isRequired,
    cityState: PropTypes.string.isRequired,
    eventDataForMember: PropTypes.object.isRequired,
    eventFromAPI: PropTypes.object.isRequired,
    fullProgramCartItem: PropTypes.object,
    fullProgramRequired: PropTypes.bool,
    handleDuesPromptClick: PropTypes.func,
    hasMembershipsInCart: PropTypes.bool,
    isLoading: PropTypes.bool,
    seatsRemaining: PropTypes.number,
    setIsLoading: PropTypes.func,
    onRemoveClick: PropTypes.func.isRequired,
};

const CartItemEventLayout = ({ onComponentLoaded = () => {}, setOptionalFullProgramEvents }) => {
    // -----------------------------
    // State
    // -----------------------------

    const { eventDataForMember, eventRegistrationData } = useContext(CartItemEventContext);
    const { isLoading, setIsLoading } = useContext(LoadingContext);
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [eventImage, setEventImage] = useState();
    const [cityState, setCityState] = useState();
    const [fullProgramCartItem, setFullProgramCartItem] = useState();
    const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
    const [eventSessionsLoaded, setEventSessionsLoaded] = useState(false);
    const [registeredEvents, setRegisteredEvents] = useState([]);
    const [status, setStatus] = useState(null);
    const [paidThru, setPaidThru] = useState(null);
    const [hasMembershipsInCart, setHasMembershipsInCart] = useState(false);
    const [fullProgramRequired, setFullProgramRequired] = useState();
    const [seatsRemaining, setSeatsRemaining] = useState();

    // -----------------------------
    // Hooks
    // -----------------------------

    const { viewer } = useViewer();
    const { event: eventFromGraphQL } = useEventFromGraph({
        code: eventDataForMember.code,
        fields: 'recordId code image',
    });

    const { openModal: openDeleteConfirmationModal } = useDeleteCartItemModal();
    const isScreenMinMd = useScreenMinMd();
    const isScreenSmDown = useScreenSmDown();

    // -----------------------------
    // Effects
    // -----------------------------

    useEffect(() => {
        if (!isFirstLoad || !eventRegistrationData?.cart || !eventRegistrationData?.event) {
            return;
        }

        if (isFirstLoad) {
            setIsFirstLoad(false);
            return;
        }

        eventRegistrationData.refetchRegistrantClass();
    }, [isFirstLoad, eventRegistrationData]);

    useEffect(() => {
        if (!viewer) return;

        setRegisteredEvents(viewer.events?.filter(event => !!event && !!event.code).map(event => event.code) || []);
        setStatus(viewer.status);
        setPaidThru(viewer.paidThru);
    }, [viewer]);

    // Notify parent when all data has loaded
    useEffect(() => {
        if (getIsReadyForRender() && eventSessionsLoaded) {
            onComponentLoaded();
        }
    }, [getIsReadyForRender, eventSessionsLoaded, onComponentLoaded]);

    // Pick the FP item out of the cart for member/event
    useEffect(() => {
        if (!eventDataForMember) {
            return;
        }

        setFullProgramCartItem(
            eventDataForMember.sessions.find(session => session.product_type === productTypes.EVENT) || null
        );
    }, [eventDataForMember]);

    // Parse out city/state when the event API data is available
    useEffect(() => {
        if (!eventRegistrationData?.event) {
            return;
        }

        const city = eventRegistrationData.event.building?.city;
        const state = eventRegistrationData.event.building?.state_province?.code;
        setCityState(getCityStateDisplay({ city, state }));
    }, [eventRegistrationData]);

    // Parse out main image when event GraphQL data is available
    useEffect(() => {
        if (isUndefined(eventFromGraphQL)) {
            return;
        }

        setEventImage(eventFromGraphQL?.image || null);
    }, [eventFromGraphQL]);

    useEffect(() => {
        if (!eventDataForMember.memberId || !eventRegistrationData) {
            return;
        }

        setHasMembershipsInCart(
            getDoesCartHaveMemberships({ cart: eventRegistrationData.cart, memberId: eventDataForMember.memberId })
        );
    }, [eventRegistrationData, eventDataForMember.memberId]);

    // -----------------------------
    // Helpers
    // -----------------------------

    const getIsReadyForRender = useCallback(() => {
        if (
            !eventRegistrationData?.cart ||
            !eventRegistrationData?.event ||
            isUndefined(eventImage) ||
            isUndefined(cityState)
        ) {
            return false;
        }

        return true;
    }, [eventRegistrationData, eventImage, cityState]);

    const removeEventItem = () => {
        openDeleteConfirmationModal(eventRegistrationData?.event.title, async () => {
            setIsLoading(true);

            for (const session of eventDataForMember.sessions) {
                await deleteCartItem({
                    cartId: eventRegistrationData?.cart.id,
                    cartItemId: session.id,
                    viewerId: viewer.recordId,
                });
            }

            await eventRegistrationData?.refetchCart();

            setIsLoading(false);
        });
    };

    const handleDuesPromptClick = async () => {
        await addMembershipToCart({
            cart: eventRegistrationData?.cart,
            memberId: eventRegistrationData?.member?.id,
            viewerId: viewer.recordId,
            years: membershipYears.THREE,
        });

        // Refresh cart after update
        await eventRegistrationData?.refetchCart();
    };

    // -----------------------------
    // Render
    // -----------------------------

    const memberStatus = getMemberStatusText(status, paidThru);
    const isMemberActive = memberStatus === 'Active' || memberStatus === 'Expiring Soon';

    if (!getIsReadyForRender()) {
        return null;
    }

    return (
        <div>
            <div>
                <div className="flex items-start">
                    {isScreenMinMd && (
                        <div className="mr-1.5rem pt-0.375rem">
                            <ImageStyled src={eventImage || '/assets/images/event-icon.svg'} />
                        </div>
                    )}

                    <div className="flex-1">
                        <BodyMedium>{eventRegistrationData.event.title}</BodyMedium>

                        {isScreenMinMd && (
                            <div className="mt-0.25rem">
                                <HeaderElements
                                    cartItems={eventRegistrationData.cart.items}
                                    cityState={cityState}
                                    eventDataForMember={eventDataForMember}
                                    eventFromAPI={eventRegistrationData.event}
                                    fullProgramCartItem={fullProgramCartItem}
                                    fullProgramRequired={fullProgramRequired}
                                    handleDuesPromptClick={handleDuesPromptClick}
                                    isLoading={isLoading}
                                    onRemoveClick={() => removeEventItem()}
                                    seatsRemaining={seatsRemaining}
                                    setIsLoading={setIsLoading}
                                />
                            </div>
                        )}
                    </div>

                    <div className="ml-1.5rem">
                        <CartItemEventPrices />
                    </div>
                </div>

                {isScreenSmDown && (
                    <div className="mt-0.25rem">
                        <HeaderElements
                            cartItems={eventRegistrationData.cart.items}
                            cityState={cityState}
                            eventDataForMember={eventDataForMember}
                            eventFromAPI={eventRegistrationData.event}
                            fullProgramCartItem={fullProgramCartItem}
                            fullProgramRequired={fullProgramRequired}
                            hasMembershipsInCart={hasMembershipsInCart}
                            handleDuesPromptClick={handleDuesPromptClick}
                            isMemberActive={isMemberActive}
                            isLoading={isLoading}
                            onRemoveClick={() => removeEventItem()}
                            setIsLoading={setIsLoading}
                        />
                    </div>
                )}

                <div style={{ marginLeft: isScreenMinMd ? '88px' : 0 }}>
                    <CartItemEventSessions
                        fullProgramRequired={fullProgramRequired}
                        onComponentLoaded={() => setEventSessionsLoaded(true)}
                        registeredEvents={registeredEvents}
                        setFullProgramRequired={setFullProgramRequired}
                        setOptionalFullProgramEvents={setOptionalFullProgramEvents}
                        setSeatsRemaining={setSeatsRemaining}
                    />

                    {(eventRegistrationData?.member?.company || eventRegistrationData?.member?.deferred_company) && (
                        <UpsellContainerStyled>
                            {!eventDataForMember.sessionsOnly && (
                                <>
                                    <UpsellDividerStyled />
                                    <CartUpsell
                                        text="Register a Co-Worker For This Event"
                                        onClick={() => setIsUpsellModalOpen(true)}
                                    />
                                </>
                            )}

                            <CartItemEventDiscountUpsell
                                eventCode={eventDataForMember.code}
                                memberId={eventDataForMember.memberId}
                            />

                            <UpsellDividerStyled />
                        </UpsellContainerStyled>
                    )}
                </div>
            </div>

            {(eventRegistrationData?.member?.company || eventRegistrationData?.member?.deferred_company) && (
                <CartUpsellEventModal
                    eventCode={eventDataForMember.code}
                    isOpen={isUpsellModalOpen}
                    onClose={() => setIsUpsellModalOpen(false)}
                />
            )}
        </div>
    );
};

CartItemEventLayout.propTypes = {
    onComponentLoaded: PropTypes.func,
    setOptionalFullProgramEvents: PropTypes.func,
};

export default CartItemEventLayout;
